import { useDispatch, useSelector } from 'react-redux';
import { ExportType, Values } from '../interfaces';
import {
  confirmPayout,
  exportPayoutDetail,
  exportPayouts,
  getPayoutFee,
  getRequiredFields,
  getBanks,
  validatePartialPayout,
  getPayoutDetail,
  getPayouts,
  resetCreatePayout,
  sendRequestPayout,
  setCreatePayoutData,
  setCreatePayoutStep,
  setCreatePayoutSubView,
  setPayoutDetail,
  startCreatePayout,
  showExportPayouts,
  verifyOtpConfigured,
  getPayoutCountryConfigurations,
  getPayoutCurrencies,
  getPayoutContacts,
  getPayoutContactsList,
  getPayoutContactValidationLink,
  revalidatePayoutContact,
  deletePayoutContactBankAccount,
  setPayoutContactBankAccountPreferred,
  loadBeneficiaryRequirements,
  loadBeneficiaryBanks,
  createPayoutBeneficiary,
  addPayoutBeneficiaryBankAccount,
  updatePayoutBeneficiaryBankAccount,
  showExportPayoutContacts,
  exportPayoutContacts,
} from './actions';
import { PayoutsSelectors } from './selectors';
import {
  AddBeneficiaryBankAccountRequest,
  CreateBeneficiaryRequest,
  CreateBeneficiaryResponse,
  CreatePayoutData,
  Filters,
  FlowType,
  Payout,
  PayoutCreationStep,
  RequiredFieldsResponse,
} from './interfaces';

export const usePayouts = () => {
  const dispatch = useDispatch();

  return {
    loading: useSelector(PayoutsSelectors.getLoading),
    error: useSelector(PayoutsSelectors.getError),
    allPayouts: useSelector(PayoutsSelectors.getAllPayouts),
    requestPayout: useSelector(PayoutsSelectors.getRequestPayout),
    export: useSelector(PayoutsSelectors.getExport),
    exportType: useSelector(PayoutsSelectors.getExportType),
    createPayout: useSelector(PayoutsSelectors.getCreatePayout),
    payoutContacts: useSelector(PayoutsSelectors.getPayoutContacts),
    contactsList: useSelector(PayoutsSelectors.getPayoutContactsList),
    contactsExport: useSelector(PayoutsSelectors.getContactsExport),
    isOtpConfigured: useSelector(PayoutsSelectors.getVerifyOtpConfigured),
    getPayouts: (page?: number, filter?: Filters) => dispatch(getPayouts(page, filter)),
    getBanks: (country: string) => dispatch(getBanks(country)),
    sendRequestPayout: (values: Values) => dispatch(sendRequestPayout(values)),
    setPayoutDetail: (data: Payout) => dispatch(setPayoutDetail(data)),
    getPayoutDetail: (id: number) => dispatch(getPayoutDetail(id)),
    exportPayoutDetail: (id: number) => dispatch(exportPayoutDetail(id)),
    payoutDetail: useSelector(PayoutsSelectors.getPayoutDetail),
    showExportPayouts: (show: boolean) => dispatch(showExportPayouts(show)),
    exportPayouts: (filter: string, type: ExportType) => dispatch(exportPayouts(filter, type)),
    startCreatePayout: () => dispatch(startCreatePayout()),
    setCreatePayoutStep: (step: PayoutCreationStep) => dispatch(setCreatePayoutStep(step)),
    setCreatePayoutSubView: (subView: 'country' | 'bank-data') => dispatch(setCreatePayoutSubView(subView)),
    setCreatePayoutData: (data: Partial<CreatePayoutData> & { bankOptions?: Array<{ code: string; name: string }> }) =>
      dispatch(setCreatePayoutData(data)),
    getRequiredFields: (country: string, flowType: FlowType) => dispatch(getRequiredFields(country, flowType)),
    getPayoutFee: (amount: number, currency: string, country: string) => dispatch(getPayoutFee(amount, currency, country)),
    validatePartialPayout: (values: Record<string, string | number>) => dispatch(validatePartialPayout(values)),
    confirmPayout: (otpCode: string) => dispatch(confirmPayout(otpCode)),
    resetCreatePayout: () => dispatch(resetCreatePayout()),
    verifyOtpConfigured: () => dispatch(verifyOtpConfigured()),
    getPayoutCountryConfigurations: () => dispatch(getPayoutCountryConfigurations()),
    getPayoutCurrencies: (country: string) => dispatch(getPayoutCurrencies(country)),
    getPayoutContacts: (mid: string) => dispatch(getPayoutContacts(mid)),
    getPayoutContactsList: (mid: string) => dispatch(getPayoutContactsList(mid)),
    getPayoutContactValidationLink: (
      merchantId: string,
      beneficiaryId: string,
      onSuccess: (validationLink: string) => void,
      onError?: () => void
    ) => dispatch(getPayoutContactValidationLink(merchantId, beneficiaryId, onSuccess, onError)),
    revalidatePayoutContact: (
      merchantId: string,
      beneficiaryId: string,
      onSuccess: () => void,
      onError?: () => void
    ) => dispatch(revalidatePayoutContact(merchantId, beneficiaryId, onSuccess, onError)),
    deletePayoutContactBankAccount: (
      merchantId: string,
      beneficiaryId: string,
      accountId: string,
      onSuccess: () => void,
      onError?: () => void
    ) => dispatch(deletePayoutContactBankAccount(merchantId, beneficiaryId, accountId, onSuccess, onError)),
    setPayoutContactBankAccountPreferred: (
      merchantId: string,
      beneficiaryId: string,
      accountId: string,
      onSuccess: () => void,
      onError?: () => void
    ) => dispatch(setPayoutContactBankAccountPreferred(merchantId, beneficiaryId, accountId, onSuccess, onError)),
    loadBeneficiaryRequirements: (
      country: string,
      flowType: FlowType,
      onSuccess: (response: RequiredFieldsResponse) => void,
      onError: (error: any) => void
    ) => dispatch(loadBeneficiaryRequirements(country, flowType, onSuccess, onError)),
    loadBeneficiaryBanks: (
      country: string,
      onSuccess: (banks: Array<{ code: string; name: string }>) => void,
      onError: (error: any) => void
    ) => dispatch(loadBeneficiaryBanks(country, onSuccess, onError)),
    createPayoutBeneficiary: (
      merchantId: string,
      data: CreateBeneficiaryRequest,
      onSuccess: (response: CreateBeneficiaryResponse) => void,
      onError: (error: any) => void
    ) => dispatch(createPayoutBeneficiary(merchantId, data, onSuccess, onError)),
    addPayoutBeneficiaryBankAccount: (
      merchantId: string,
      beneficiaryId: string,
      data: AddBeneficiaryBankAccountRequest,
      onSuccess: (response: any) => void,
      onError: (error: any) => void
    ) => dispatch(addPayoutBeneficiaryBankAccount(merchantId, beneficiaryId, data, onSuccess, onError)),
    updatePayoutBeneficiaryBankAccount: (
      merchantId: string,
      beneficiaryId: string,
      accountId: string,
      data: AddBeneficiaryBankAccountRequest,
      onSuccess: (response: any) => void,
      onError: (error: any) => void
    ) => dispatch(updatePayoutBeneficiaryBankAccount(merchantId, beneficiaryId, accountId, data, onSuccess, onError)),
    showExportPayoutContacts: (show: boolean) => dispatch(showExportPayoutContacts(show)),
    exportPayoutContacts: (merchantId: string, filter: string, type: ExportType) =>
      dispatch(exportPayoutContacts(merchantId, filter, type)),
  };
};
